import { useCallback, useEffect, useRef, useState } from "react";
import { errorText } from "./formsApi";

/**
 * Keep a value saved. About `delay` ms after it changes it is sent; one
 * save at a time, and a change made during a save is saved right after it.
 * An error is shown and stops until the next change. Leaving the page saves
 * what is pending. Call markSaved with the value as loaded.
 *
 * pending: what is on screen is not stored yet, or a save is on its way.
 * save(): store it now; resolves false on an error.
 */
export function useAutosave<T>(
  value: T | undefined,
  send: (value: T) => Promise<unknown>,
  delay: number
) {
  const current = value === undefined ? undefined : JSON.stringify(value);
  const [saved, setSaved] = useState<string>();
  const [error, setError] = useState<string>();
  // The save chain outlives renders, so it reads these.
  const latest = useRef(current);
  latest.current = current;
  const sendLatest = useRef(send);
  sendLatest.current = send;
  const stored = useRef<string>();
  const inFlight = useRef<Promise<boolean> | null>(null);

  const save = useCallback((): Promise<boolean> => {
    if (inFlight.current) return inFlight.current;
    const sent = latest.current;
    setError(undefined);
    if (sent === undefined || sent === stored.current) {
      // Back to what is stored (an undo): nothing to send.
      setSaved(stored.current);
      return Promise.resolve(true);
    }
    // Not "Saved" while this one is on its way, even if it is undone.
    setSaved(undefined);
    const run = sendLatest.current(JSON.parse(sent)).then(
      () => {
        stored.current = sent;
        setSaved(sent);
        inFlight.current = null;
        return save(); // edits made during the save
      },
      (e) => {
        inFlight.current = null;
        setError(
          errorText(e, "Not saved. Check your connection and try again.")
        );
        return false;
      }
    );
    inFlight.current = run;
    return run;
  }, []);

  const pending = current !== undefined && current !== saved;

  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(save, delay);
    return () => clearTimeout(timer);
  }, [current, pending, save, delay]);

  // Leaving by any route saves what is pending.
  useEffect(
    () => () => {
      save();
    },
    [save]
  );

  const markSaved = useCallback((loaded: T) => {
    stored.current = JSON.stringify(loaded);
    setSaved(stored.current);
  }, []);

  return { pending, error, save, markSaved };
}
